import { ListingResultsContext } from './listing-results-context';
import { userInfoAtom } from 'app/atoms';
import { ListingOfferCard } from 'app/components/listing-offer-card';
import { ListingOfferCardHorizontal } from 'app/components/listing-card-horizontal.large';
import {
  ListingOfferCardLarge,
  OFFER_CARD_GRID_WIDTH as LARGE_OFFER_CARD_GRID_WIDTH,
} from 'app/components/listing-offer-card.large';
import { ListingRequestCard, ListingRequestCardHighest } from 'app/components/listing-request-card';
import { useTutorialRef } from 'app/components/tutorial-overlay';
import type { TopLoadingIndicatorState } from 'app/components/top-loading-indicator';
import { useTopLoadingIndicator } from 'app/components/top-loading-indicator';
import { BottomSheet } from 'app/design/primitives/bottom-sheet';
import { Box, Column, Row } from 'app/design/primitives/box';
import { Button, PressableType } from 'app/design/primitives/button';
import type { FlashOrFlatList, ListPageProps } from 'app/design/primitives/page/list';
import { PageListComponentType } from 'app/design/primitives/page/list.constants';
import { ListPage } from 'app/design/primitives/page/list';
import { Text } from 'app/design/primitives/text';

import { useRoute } from 'app/hooks/navigation';
import { useFlashListBenchmark } from 'app/hooks/use-flash-list-benchmark';
import { Event, track } from 'app/lib/analytics';
import { mergeSelectedListingIds } from 'app/lib/listing-selection';
import { useAllListings } from 'app/network/listing';
import { useUser } from 'app/network/user';
import { CreateOfferBanner } from 'app/screens/billboard/all-listings/components/create-offer-banner';
import { CreateRequestBanner } from 'app/screens/billboard/all-listings/components/create-request-banner';
import { type Listing, ListingType, ListingsSortBy, type FindAllListingsDTO } from 'app/types/listing';
import type { ListingOfferCardProps } from 'app/types/listing-offer-card-props';
import { TutorialRef } from 'app/types/tutorial';
import { FeatureFlag, isFeatureEnabled, isWhiteLabel } from 'app/utils/helpers';
import { useRegisterLoading } from 'app/utils/performance/screen-perf-context';
import { isMobileNative, useUISize } from 'app/utils/web-helpers';
import { useAtomValue } from 'jotai/index';
import type { ComponentType, Dispatch, JSX, PropsWithChildren, RefObject, SetStateAction } from 'react';
import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ActivityIndicator,
  Keyboard,
  type LayoutChangeEvent,
  type NativeScrollEvent,
  type NativeSyntheticEvent,
  Pressable,
  StyleSheet,
  type StyleProp,
  View,
  type ViewProps,
  type ViewStyle,
} from 'react-native';
import Animated, { Easing, useAnimatedStyle, withTiming } from 'react-native-reanimated';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import type { BenchmarkParams, BenchmarkResult, FlashListRef } from '@shopify/flash-list';

import { ListingSkeletons } from './listing-skeletons';
// import { ListingThumbnail } from './listing-thumbnail';
import { SearchInput } from 'app/design/primitives/input/search';
import { ListingOfferCardSmallOld } from './listing-offer-card-old.small';
import {
  ListingOfferCardSmall,
  OFFER_CARD_GRID_WIDTH as SMALL_OFFER_CARD_GRID_WIDTH,
} from './listing-offer-card.small';
import { createTheme as createDSTheme, type Theme } from 'app/shopify';
import { ICON_STROKE_WIDTH } from 'app/utils/constants';
import {
  IconArrowsSort,
  IconChevronLeft,
  IconChevronRight,
  IconLayoutColumns,
  IconList,
} from '@tabler/icons-react-native';
const theme = createDSTheme();

interface ListingListPresentation {
  user?: 'visible' | 'hidden';
  selection?: 'enabled' | 'disabled';
  listingSubtype?: 'visible' | 'hidden';
  requestBanner?: 'visible' | 'hidden';
  offerBanner?: 'visible' | 'hidden';
  padding?: 'none' | 'page';
  verticalScrollIndicator?: 'visible' | 'hidden';
  searchBar?: 'visible' | 'hidden';
  navigation?: 'buttons' | 'none';
  requestCard?: 'standard' | 'highest';
}

interface ListingListProps extends Omit<ListPageProps<Listing>, 'data' | 'renderItem' | 'onScroll'> {
  filters?: FindAllListingsDTO & { isLoggedIn?: boolean };
  excludeIds?: string[];
  fetchListingsEnabled?: boolean;
  trackSearchPerformedEvent?: boolean;
  showUser?: boolean;
  inlinePrompts?: { position: number; element: JSX.Element }[];
  isLoadingState?: TopLoadingIndicatorState;
  isSelecting?: boolean;
  selectAll?: boolean;
  onSelectableListingIdsChange?: (ids: Listing['id'][]) => void;
  selectedRowsState?: [Listing['id'][], Dispatch<SetStateAction<Listing['id'][]>>];
  isEmptyState?: [boolean, Dispatch<SetStateAction<boolean>>];
  allowSort?: boolean;
  sortState?: [ListingsSortBy, Dispatch<SetStateAction<ListingsSortBy>>];
  sortByStartDate?: boolean;
  onScroll?: (event: NativeSyntheticEvent<NativeScrollEvent>, numListings: number) => void;
  innerRef?: RefObject<FlashOrFlatList<Listing, null>>;
  hideListingsSubtype?: boolean;
  i18nKey?: string | null;
  throwOnError?: boolean;
  metricsLabel?: string;
  showRequestsBanner?: boolean;
  showOffersBanner?: boolean;
  alwaysShowFooter?: boolean;
  useHorizontalCard?: boolean;
  onBlockDates?: () => void;
  onChangeLoanFee?: () => void;
  onHide?: () => void;
  onUnhide?: () => void;
  onDelete?: () => void;
  setListingForMenuAction?: Dispatch<SetStateAction<Listing['id'] | null>>;
  withPadding?: boolean;
  /** Maximum width of grid rows; the scroll view and footer remain full width. */
  maxGridWidth?: number;
  showButtonsNavigation?: boolean;
  showHighestCard?: boolean;
  onCategoryPress?: (categoryId: string) => void;
  categoryName?: string;
  showVerticalScrollIndicator?: boolean;
  showSearchBar?: boolean;
  showResultsCount?: boolean;
  resultsHeaderActions?: React.ReactNode;
  searchQuery?: string;
  onSearchQueryChange?: Dispatch<SetStateAction<string>>;
  listFormat?: 'list' | 'grid';
  setListFormat?: Dispatch<SetStateAction<'list' | 'grid'>>;
  /** Horizontal gap between listing columns in grid mode. Adds horizontal padding around each card so adjacent columns are separated by this spacing token. */
  horizontalGap?: keyof Theme['spacing'];
  /** Override the offer card component. When provided, replaces the built-in card selection logic for offer-type listings. */
  card?: React.ComponentType<ListingOfferCardProps>;
  benchmarkFlashList?: boolean;
  benchmarkFlashListParams?: BenchmarkParams;
  onBenchmarkComplete?: (result: BenchmarkResult) => void;
  data?: Listing[]; // For when the parent wants to manage the data and bypass useAllListings
  isLoading?: boolean;
  isEmpty?: boolean;
  isFetchingNextPage?: boolean;
  numListingsTotal?: number;
  searchAsYouType?: boolean;
  queryContent?: React.ReactNode;
  onListingHoverChange?: (listing?: Listing) => void;
  highlightedListingId?: Listing['id'];
  itemHorizontalSpacing?: number;
  /** Compatibility with the compiler-refactored callers on this branch. */
  presentation?: ListingListPresentation;
  loadingIndicatorState?: TopLoadingIndicatorState;
  byPassFixedHeight?: boolean;
}

interface ListingListState {
  items: Listing[];
  numListingsTotal: number;
  isLoading: boolean;
  isPending: boolean;
  isFetching: boolean;
  isRefetching: boolean;
  isFetchingNextPage: boolean;
  isSelectAllPartial: boolean;
  isSelectingAll: boolean;
  onRefresh?: ListPageProps<Listing>['onRefresh'];
  refreshing: boolean;
  onEndReached?: ListPageProps<Listing>['onEndReached'];
}

interface HighlightableListingCardProps extends Pick<ViewProps, 'onPointerEnter' | 'onPointerLeave'> {
  isDimmed: boolean;
  isHighlighted: boolean;
  highlightedStyle: StyleProp<ViewStyle>;
}

function HighlightableListingCard({
  children,
  highlightedStyle,
  isDimmed,
  isHighlighted,
  onPointerEnter,
  onPointerLeave,
}: PropsWithChildren<HighlightableListingCardProps>) {
  const animatedStyle = useAnimatedStyle(() => {
    const animation = { duration: 250, easing: Easing.out(Easing.cubic) };

    return {
      opacity: withTiming(isDimmed ? 0.6 : 1, animation),
    };
  }, [isDimmed]);

  return (
    <Animated.View
      onPointerEnter={onPointerEnter}
      onPointerLeave={onPointerLeave}
      style={[animatedStyle, isHighlighted ? highlightedStyle : undefined]}
    >
      {children}
    </Animated.View>
  );
}

// How long filters must be stable before TanStack Query is allowed to auto-fetch.
// Absorbs startup churn: stale BFF sort pref arriving before HTTP sort pref, address
// background-refetch, etc.
const LISTING_FETCH_DEBOUNCE_MS = 300;
const MAX_SELECT_ALL_AUTO_FETCH_REQUESTS = 10;
const MAX_GRID_CARD_WIDTH_MULTIPLIER = 1.3;
const EMPTY_LISTINGS: Listing[] = [];
interface FirstOfferTutorialCardProps extends ListingOfferCardProps {
  CardComponent: ComponentType<ListingOfferCardProps>;
}

interface PartialSearchBarProps {
  value: string;
  onSearch: (query: string) => void;
  searchAsYouType?: boolean;
}

const PartialSearchBar = memo(function PartialSearchBarComponent({
  value,
  onSearch,
  searchAsYouType,
}: PartialSearchBarProps) {
  const { t } = useTranslation('all-listings');

  return (
    <Column marginBottom="spacing.general.x2">
      <SearchInput
        initialValue={value}
        onSubmit={onSearch}
        placeholder={t('partialSearchBar.placeholder')}
        searchAsYouType={searchAsYouType}
        submitOnEmptyChange
      />
    </Column>
  );
});

const FirstOfferTutorialCard = memo(function FirstOfferTutorialCardComponent({
  CardComponent,
  ...props
}: FirstOfferTutorialCardProps) {
  const { continueTutorial, viewRef } = useTutorialRef(TutorialRef.FirstOfferInList);

  return <CardComponent {...props} continueTutorial={continueTutorial} isFirst tutorialViewRef={viewRef} />;
});

export function ListingList(props: ListingListProps) {
  const { presentation, loadingIndicatorState, byPassFixedHeight: _byPassFixedHeight, ...legacyProps } = props;
  const normalizedProps: ListingListProps = {
    ...legacyProps,
    isLoadingState: props.isLoadingState ?? loadingIndicatorState,
    showUser: presentation?.user ? presentation.user === 'visible' : props.showUser,
    isSelecting: presentation?.selection ? presentation.selection === 'enabled' : props.isSelecting,
    hideListingsSubtype: presentation?.listingSubtype
      ? presentation.listingSubtype === 'hidden'
      : props.hideListingsSubtype,
    showRequestsBanner: presentation?.requestBanner
      ? presentation.requestBanner === 'visible'
      : props.showRequestsBanner,
    showOffersBanner: presentation?.offerBanner ? presentation.offerBanner === 'visible' : props.showOffersBanner,
    withPadding: presentation?.padding ? presentation.padding === 'page' : props.withPadding,
    showVerticalScrollIndicator: presentation?.verticalScrollIndicator
      ? presentation.verticalScrollIndicator === 'visible'
      : props.showVerticalScrollIndicator,
    showSearchBar: presentation?.searchBar ? presentation.searchBar === 'visible' : props.showSearchBar,
    showButtonsNavigation: presentation?.navigation
      ? presentation.navigation === 'buttons'
      : props.showButtonsNavigation,
    showHighestCard: presentation?.requestCard ? presentation.requestCard === 'highest' : props.showHighestCard,
  };

  if ('data' in normalizedProps) {
    return <ControlledListingList {...normalizedProps} data={normalizedProps.data ?? []} />;
  }

  return <UncontrolledListingList {...normalizedProps} />;
}

function ControlledListingList({
  data = [],
  isLoading = false,
  isEmpty,
  isFetchingNextPage = false,
  numListingsTotal,
  isEmptyState,
  isLoadingState,
  metricsLabel = 'anonymous',
  onRefresh,
  onEndReached,
  refreshing = false,
  ...props
}: Readonly<ListingListProps>) {
  useEffect(() => {
    if (!isEmptyState) return;
    isEmptyState[1](isEmpty ?? data.length === 0);
  }, [data.length, isEmpty, isEmptyState]);

  useEffect(() => {
    if (!isLoadingState) return;
    isLoadingState.setIsLoading(isLoading && data.length === 0);
  }, [data.length, isLoading, isLoadingState]);

  useRegisterLoading(isLoading, metricsLabel);

  return (
    <ListingListContent
      {...props}
      filters={props.filters}
      listingListState={{
        items: data,
        numListingsTotal: numListingsTotal ?? data.length,
        isLoading,
        isPending: isLoading && data.length === 0,
        isFetching: isLoading || refreshing,
        isRefetching: refreshing,
        isFetchingNextPage,
        isSelectAllPartial: false,
        isSelectingAll: false,
        onRefresh,
        refreshing,
        onEndReached,
      }}
    />
  );
}

function UncontrolledListingList({
  filters,
  fetchListingsEnabled = true,
  trackSearchPerformedEvent = false,
  isEmptyState,
  isLoadingState,
  throwOnError = true,
  metricsLabel = 'anonymous',
  selectAll,
  ...props
}: Readonly<ListingListProps>) {
  const { effectiveFilters, searchQuery, setSearchQuery } = useListingSearch(props, filters);

  // Serialize filters to a string so the effect below uses value equality instead of
  // reference equality. The parent recreates the filters object on every render
  // (getListingsFilters returns a new object each call), so using the object as a dep
  // would re-run the effect on every render — even when content hasn't changed — causing
  // an infinite fetch loop. Using the JSON string means React only re-runs the effect
  // when filter content actually changes.
  const filtersJson = JSON.stringify(effectiveFilters);

  // Use the identity of the current inputs as the debounce key. A filter or enabled-state
  // change creates a new key, making `stableEnabled` false immediately without synchronously
  // setting state in the effect.
  const debounceKey = useMemo(() => ({ fetchListingsEnabled, filtersJson }), [fetchListingsEnabled, filtersJson]);
  const [enabledDebounceKey, setEnabledDebounceKey] = useState<typeof debounceKey | null>(null);
  const stableEnabled = fetchListingsEnabled && enabledDebounceKey === debounceKey;
  const debounceEnableRef = useRef<ReturnType<typeof setTimeout>>(null);
  // Holds the latest resetPagination from useAllListings so the debounce
  // setTimeout can call it without a stale closure. Updated every render (below).
  const resetPaginationRef = useRef<(() => void) | undefined>(undefined);

  useEffect(() => {
    if (!fetchListingsEnabled) return;

    if (debounceEnableRef.current !== null) clearTimeout(debounceEnableRef.current);
    debounceEnableRef.current = setTimeout(() => {
      debounceEnableRef.current = null;
      // Trim any previously-cached pages for this query key down to 1 before
      // enabling TanStack Query. Without this, revisiting a filter set that was
      // previously scrolled to page N would cause N refetch calls (one per page).
      resetPaginationRef.current?.();
      setEnabledDebounceKey(debounceKey);
    }, LISTING_FETCH_DEBOUNCE_MS);

    return () => {
      if (debounceEnableRef.current !== null) {
        clearTimeout(debounceEnableRef.current);
        debounceEnableRef.current = null;
      }
    };
  }, [debounceKey, fetchListingsEnabled]);

  const listingsQuery = useAllListings(
    effectiveFilters,
    'ListingList',
    {
      throwOnError,
      enabled: stableEnabled,
    },
    trackSearchPerformedEvent,
  );
  // Keep the ref current so the debounce callback always calls resetPagination
  // for the query key that is active at the time the timeout fires.
  useEffect(() => {
    resetPaginationRef.current = listingsQuery.resetPagination;
  }, [listingsQuery.resetPagination]);

  useEffect(() => {
    if (!isEmptyState) return;
    isEmptyState[1](!listingsQuery.data?.pages[0] || listingsQuery.data.pages[0].results.length === 0);
  }, [isEmptyState, listingsQuery.data]);

  useTopLoadingIndicator(listingsQuery, isLoadingState);

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage } = listingsQuery;
  const { isSelectAllPartial, isSelectingAll } = useSelectAllListingPages(
    selectAll,
    hasNextPage,
    isFetchingNextPage,
    filtersJson,
    stableEnabled,
    fetchNextPage,
  );

  const items = useMemo(() => {
    const allListings = data?.pages.flatMap((page) => page.results) ?? [];

    // Deduplicate across pages: each page is fetched independently, so items
    // can shift between fetches and appear in multiple cached pages.
    const seenIds = new Set<string>();
    return allListings.filter((listing) => {
      if (seenIds.has(listing.id)) return false;
      seenIds.add(listing.id);
      // Dismissed-only is a requests feature; offers should not disappear from management lists.
      if (
        effectiveFilters?.type === ListingType.request &&
        !effectiveFilters.dismissedOnly &&
        listing.dismissed &&
        listing.userId !== effectiveFilters.userId
      ) {
        return false;
      }
      return true;
    });
  }, [data?.pages, effectiveFilters]);

  const lastFetchedPageCountRef = useRef(0);

  useEffect(() => {
    lastFetchedPageCountRef.current = 0;
  }, [filters?.type, filters?.dismissedOnly]);

  useEffect(() => {
    const firstPage = data?.pages[0];
    if (!firstPage) return;

    const pageCount = data.pages.length;
    const shouldBackfillRequests =
      filters?.type === ListingType.request &&
      !filters.dismissedOnly &&
      items.length < firstPage.take &&
      hasNextPage &&
      !isFetchingNextPage &&
      pageCount > lastFetchedPageCountRef.current;

    if (!shouldBackfillRequests) return;

    lastFetchedPageCountRef.current = pageCount;
    void fetchNextPage();
  }, [
    data?.pages,
    fetchNextPage,
    filters?.dismissedOnly,
    filters?.type,
    hasNextPage,
    isFetchingNextPage,
    items.length,
  ]);

  const onEndReached = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      void fetchNextPage();
    }
  }, [fetchNextPage, hasNextPage, isFetchingNextPage]);

  const numListingsTotal = useMemo(() => listingsQuery.data?.pages[0]?.total ?? 0, [listingsQuery.data?.pages]);
  const filterSearch = effectiveFilters?.search?.trim() ?? '';
  const awaitingSearchResults =
    filterSearch.length > 0 &&
    fetchListingsEnabled &&
    stableEnabled &&
    (!listingsQuery.isFetched || (listingsQuery.isFetching && !listingsQuery.isFetchingNextPage));
  const displayItems = awaitingSearchResults ? EMPTY_LISTINGS : items;

  useRegisterLoading(listingsQuery.isPending, metricsLabel);

  return (
    <ListingListContent
      {...props}
      filters={effectiveFilters}
      selectAll={Boolean(selectAll && !isSelectAllPartial)}
      searchQuery={searchQuery}
      onSearchQueryChange={setSearchQuery}
      listingListState={{
        items: displayItems,
        numListingsTotal: awaitingSearchResults ? 0 : numListingsTotal,
        isLoading:
          awaitingSearchResults ||
          (!listingsQuery.data && listingsQuery.isFetching && !listingsQuery.isFetchingNextPage),
        isPending: listingsQuery.isPending || awaitingSearchResults,
        isFetching: listingsQuery.isFetching,
        isRefetching: listingsQuery.isRefetching,
        isFetchingNextPage: listingsQuery.isFetchingNextPage,
        isSelectAllPartial,
        isSelectingAll,
        onRefresh: listingsQuery.resetThenRefetch,
        refreshing: false,
        onEndReached,
      }}
    />
  );
}

function useSelectAllListingPages(
  selectAll: boolean | undefined,
  hasNextPage: boolean,
  isFetchingNextPage: boolean,
  filtersJson: string,
  stableEnabled: boolean,
  fetchNextPage: ReturnType<typeof useAllListings>['fetchNextPage'],
) {
  const selectAllAutoFetchCountRef = useRef(0);
  const [hasReachedSelectAllLimit, setHasReachedSelectAllLimit] = useState(false);
  const isSelectAllPartial = Boolean(selectAll && hasReachedSelectAllLimit);

  const isSelectingAll = Boolean(selectAll && !isSelectAllPartial && (hasNextPage || isFetchingNextPage));

  useEffect(() => {
    selectAllAutoFetchCountRef.current = 0;
    const resetPartialSelectionTimeout = setTimeout(() => setHasReachedSelectAllLimit(false), 0);
    return () => clearTimeout(resetPartialSelectionTimeout);
  }, [filtersJson, selectAll]);

  useEffect(() => {
    if (!selectAll || !stableEnabled || !hasNextPage || isFetchingNextPage || isSelectAllPartial) return;

    if (selectAllAutoFetchCountRef.current >= MAX_SELECT_ALL_AUTO_FETCH_REQUESTS) {
      // Let the final fetched page merge into the selection before switching to partial mode.
      const markPartialSelectionTimeout = setTimeout(() => setHasReachedSelectAllLimit(true), 0);
      return () => clearTimeout(markPartialSelectionTimeout);
    }

    selectAllAutoFetchCountRef.current += 1;
    void fetchNextPage();
  }, [fetchNextPage, hasNextPage, isFetchingNextPage, isSelectAllPartial, selectAll, stableEnabled]);
  return { isSelectAllPartial, isSelectingAll };
}

function useListingSearch(
  props: ListingListProps,
  filters: (FindAllListingsDTO & { isLoggedIn?: boolean }) | undefined,
) {
  const [uncontrolledSearchQuery, setUncontrolledSearchQuery] = useState('');
  const controlledSearchQuery = props.searchQuery;
  const controlledSetSearchQuery = props.onSearchQueryChange;
  const isSearchControlled = controlledSearchQuery !== undefined && controlledSetSearchQuery !== undefined;
  const searchQuery = isSearchControlled ? controlledSearchQuery : uncontrolledSearchQuery;
  const setSearchQuery = isSearchControlled ? controlledSetSearchQuery : setUncontrolledSearchQuery;
  const effectiveFilters = useMemo(() => {
    if (!props.showSearchBar) return filters;
    return { ...filters, search: searchQuery.trim() };
  }, [filters, props.showSearchBar, searchQuery]);
  return { effectiveFilters, searchQuery, setSearchQuery };
}

function ListingListContent({
  queryContent,
  ...props
}: Readonly<ListingListProps & { listingListState: ListingListState }>) {
  const { listingListState, filters } = props;
  return (
    <ListingResultsContext.Provider
      value={{
        filterSearch: filters?.search?.trim() ?? '',
        isFetching: listingListState.isFetching,
        isPending: listingListState.isPending,
        isSelectAllPartial: listingListState.isSelectAllPartial,
        isSelectingAll: listingListState.isSelectingAll,
        itemsCount: listingListState.items.length,
        numListingsTotal: listingListState.numListingsTotal,
      }}
    >
      {queryContent}
      <ListingListView {...props} />
    </ListingResultsContext.Provider>
  );
}

function ListingListView({
  filters,
  ListEmptyComponent,
  ListHeaderComponent,
  ListFooterComponent,
  alwaysShowFooter = false,
  inlinePrompts,
  onScroll,
  showUser = true,
  isSelecting,
  selectAll,
  selectedRowsState,
  onSelectableListingIdsChange,
  allowSort,
  sortState,
  sortByStartDate,
  innerRef,
  hideListingsSubtype,
  i18nKey,
  showRequestsBanner = true,
  showOffersBanner = false,
  useHorizontalCard = false,
  onBlockDates,
  onChangeLoanFee,
  onHide,
  onUnhide,
  onDelete,
  setListingForMenuAction,
  onCategoryPress,
  categoryName,
  withPadding,
  maxGridWidth,
  showVerticalScrollIndicator = false,
  showSearchBar = false,
  showResultsCount = false,
  resultsHeaderActions,
  searchQuery = '',
  onSearchQueryChange,
  showButtonsNavigation = false,
  showHighestCard = false,
  listFormat,
  setListFormat,
  horizontalGap,
  benchmarkFlashList,
  benchmarkFlashListParams,
  onBenchmarkComplete,
  onScrollBeginDrag,
  card: CardOverride,
  keyExtractor: providedKeyExtractor,
  onListingHoverChange,
  highlightedListingId,
  listingListState,
  searchAsYouType = true,
  ...rest
}: Readonly<ListingListProps & { listingListState: ListingListState }>) {
  const route = useRoute();
  const { t } = useTranslation('all-listings');
  const { isLargeUI, isSmallUI } = useUISize();
  const isDisplayListForced = ['my-listings', 'neighbour-listings'].includes(route.name);
  const skipHeightUniformity = filters?.type === ListingType.request && route.name === 'landing-page';

  const effectiveDisplayMode = useMemo(() => {
    return isSmallUI && (filters?.type === ListingType.request || isDisplayListForced)
      ? 'list'
      : (listFormat ?? (isSmallUI ? 'grid' : 'list'));
  }, [filters?.type, isDisplayListForced, listFormat, isSmallUI]);

  const OfferCardComponent = getOfferCardComponent(isSmallUI, useHorizontalCard);
  const resultsTranslationKey = getListingResultsTranslationKey(i18nKey, filters);

  const userInfo = useAtomValue(userInfoAtom);
  const canPublish = useMemo(
    () => isFeatureEnabled(FeatureFlag.publishListings, userInfo?.featureFlags),
    [userInfo?.featureFlags],
  );

  const listRef = useRef<FlashOrFlatList<Listing>>(null as unknown as FlashOrFlatList<Listing>);
  const effectiveInnerRef = innerRef ?? listRef;
  const items = listingListState.items;
  const isSearchActive = showSearchBar && searchQuery.trim().length > 0;
  const {
    listWidth,
    setListWidth,
    measuredGridCardWidth,
    onGridCardLayout,
    numColumns,
    gridColumnWidth,
    gridRowPadding,
    gridItemAlignSelf,
    shouldMeasureGridCard,
  } = useListingGridLayout({
    effectiveDisplayMode,
    filters,
    useHorizontalCard,
    showHighestCard,
    isSmallUI,
    isLargeUI,
    withPadding,
    maxGridWidth,
    horizontal: rest.horizontal,
    numColumns: rest.numColumns,
    itemHorizontalSpacing: rest.itemHorizontalSpacing,
    columnWrapperStyle: rest.columnWrapperStyle,
  });
  useEffect(() => {
    effectiveInnerRef.current?.scrollToOffset({ animated: true, offset: 0 });
  }, [effectiveInnerRef, filters?.category, filters?.search]);

  useListingSelectionSync({ items, selectAll, selectedRowsState, onSelectableListingIdsChange });

  const inlinePromptByPosition = useMemo(() => {
    if (!inlinePrompts?.length) return undefined;
    return new Map(inlinePrompts.map((prompt) => [prompt.position, prompt.element]));
  }, [inlinePrompts]);

  const { renderedItems, isBenchmarkReady, isBenchmarkRunning, isUsingBenchmarkData, onStartBenchmark } =
    useFlashListBenchmark({
      enabled: Boolean(benchmarkFlashList),
      items,
      innerRef: effectiveInnerRef as RefObject<FlashListRef<Listing>>,
      benchmarkParams: benchmarkFlashListParams,
      onBenchmarkComplete,
      isPending: listingListState.isPending,
      isRefetching: listingListState.isRefetching,
    });

  const sorting = useListingSort({ filters, sortState, sortByStartDate });
  const { doShowSortOptions } = sorting;
  const numListingsTotal = listingListState.numListingsTotal;
  const filterCategory = filters?.category;
  const filterSortBy = filters?.sortBy;

  const { onNextButtonPressed, handleScroll, handleScrollBeginDrag } = useListingScroll({
    effectiveInnerRef,
    measuredGridCardWidth,
    onScroll,
    onScrollBeginDrag,
    itemCount: items.length,
  });

  const updatedListHeaderComponent = (
    <ListingResultsHeader
      ListHeaderComponent={ListHeaderComponent}
      maxGridWidth={maxGridWidth}
      showButtonsNavigation={showButtonsNavigation}
      effectiveDisplayMode={effectiveDisplayMode}
      filterCategory={filterCategory}
      onCategoryPress={onCategoryPress}
      categoryName={categoryName}
      onNextButtonPressed={onNextButtonPressed}
      showSearchBar={showSearchBar}
      onSearchQueryChange={onSearchQueryChange}
      searchQuery={searchQuery}
      searchAsYouType={searchAsYouType}
      numListingsTotal={numListingsTotal}
      isSmallUI={isSmallUI}
      showResultsCount={showResultsCount}
      allowSort={allowSort}
      resultsHeaderActions={resultsHeaderActions}
      t={t}
      resultsTranslationKey={resultsTranslationKey}
      setListFormat={setListFormat}
      filters={filters}
      isDisplayListForced={isDisplayListForced}
      filterSortBy={filterSortBy}
      doShowSortOptions={doShowSortOptions}
      canPublish={canPublish}
      showOffersBanner={showOffersBanner}
      showRequestsBanner={showRequestsBanner}
      benchmarkFlashList={benchmarkFlashList}
      isBenchmarkReady={isBenchmarkReady}
      isBenchmarkRunning={isBenchmarkRunning}
      onStartBenchmark={onStartBenchmark}
    />
  );

  const separator = useCallback(
    () => <Column height={theme.spacing['spacing.general.x4']} style={{ marginLeft: 'auto', marginRight: 'auto' }} />,
    [],
  );

  const { isMenuItemAvailable, highlightedListingCardStyle } = useListingCardPresentation();

  const renderListingItem = useCallback(
    ({ item, index }: { item: Listing; index: number }) => (
      <ListingListItem
        item={item}
        index={index}
        inlinePromptByPosition={inlinePromptByPosition}
        effectiveDisplayMode={effectiveDisplayMode}
        gridColumnWidth={gridColumnWidth}
        hideListingsSubtype={hideListingsSubtype}
        isSelecting={isSelecting}
        selectedRowsState={selectedRowsState}
        showUser={showUser}
        isMenuItemAvailable={isMenuItemAvailable}
        onBlockDates={onBlockDates}
        onChangeLoanFee={onChangeLoanFee}
        onHide={onHide}
        onUnhide={onUnhide}
        onDelete={onDelete}
        setListingForMenuAction={setListingForMenuAction}
        CardOverride={CardOverride}
        showHighestCard={showHighestCard}
        gridItemAlignSelf={gridItemAlignSelf}
        shouldMeasureGridCard={shouldMeasureGridCard}
        onGridCardLayout={onGridCardLayout}
        skipHeightUniformity={skipHeightUniformity}
        isLargeUI={isLargeUI}
        OfferCardComponent={OfferCardComponent}
        onListingHoverChange={onListingHoverChange}
        highlightedListingId={highlightedListingId}
        highlightedListingCardStyle={highlightedListingCardStyle}
        horizontalGap={horizontalGap}
      />
    ),
    [
      inlinePromptByPosition,
      effectiveDisplayMode,
      gridColumnWidth,
      hideListingsSubtype,
      isSelecting,
      selectedRowsState,
      showUser,
      isMenuItemAvailable,
      onBlockDates,
      onChangeLoanFee,
      onHide,
      onUnhide,
      onDelete,
      setListingForMenuAction,
      CardOverride,
      showHighestCard,
      gridItemAlignSelf,
      shouldMeasureGridCard,
      onGridCardLayout,
      skipHeightUniformity,
      isLargeUI,
      OfferCardComponent,
      onListingHoverChange,
      highlightedListingId,
      highlightedListingCardStyle,
      horizontalGap,
    ],
  );

  const shouldHandleScroll = Boolean(onScroll ?? showButtonsNavigation);

  return (
    <>
      {showButtonsNavigation && effectiveDisplayMode === 'list' && filterCategory !== undefined ? (
        <ListingCategoryNavigation
          filterCategory={filterCategory}
          onCategoryPress={onCategoryPress}
          categoryName={categoryName}
          onNextButtonPressed={onNextButtonPressed}
        />
      ) : null}

      <ListingResultsViewport
        listWidth={listWidth}
        setListWidth={setListWidth}
        effectiveDisplayMode={effectiveDisplayMode}
        maxGridWidth={maxGridWidth}
        gridRowPadding={gridRowPadding}
        rest={rest}
        benchmarkFlashList={benchmarkFlashList}
        numColumns={numColumns}
        withPadding={withPadding}
        separator={separator}
        listingListState={listingListState}
        isSearchActive={isSearchActive}
        items={items}
        t={t}
        ListEmptyComponent={ListEmptyComponent}
        alwaysShowFooter={alwaysShowFooter}
        ListFooterComponent={ListFooterComponent}
        updatedListHeaderComponent={updatedListHeaderComponent}
        renderedItems={renderedItems}
        effectiveInnerRef={effectiveInnerRef}
        providedKeyExtractor={providedKeyExtractor}
        isUsingBenchmarkData={isUsingBenchmarkData}
        shouldHandleScroll={shouldHandleScroll}
        handleScroll={handleScroll}
        handleScrollBeginDrag={handleScrollBeginDrag}
        renderListingItem={renderListingItem}
        showVerticalScrollIndicator={showVerticalScrollIndicator}
      />

      <ListingSortSheet sorting={sorting} />
    </>
  );
}

function ListingResultsViewport({
  listWidth,
  setListWidth,
  effectiveDisplayMode,
  maxGridWidth,
  gridRowPadding,
  rest,
  ...resultsProps
}: React.ComponentProps<typeof ListingPageResults> & {
  listWidth: number;
  setListWidth: Dispatch<SetStateAction<number>>;
  effectiveDisplayMode: 'list' | 'grid';
  maxGridWidth: ListingListProps['maxGridWidth'];
  gridRowPadding: number;
}) {
  return (
    <View
      onLayout={(event) => {
        const nextListWidth = event.nativeEvent.layout.width;
        if (nextListWidth !== listWidth) setListWidth(nextListWidth);
      }}
      style={{ flex: 1 }}
    >
      {(effectiveDisplayMode !== 'grid' || rest.horizontal || listWidth > 0) && (
        <ListingPageResults
          {...resultsProps}
          rest={{
            ...rest,
            columnWrapperStyle: [
              { paddingHorizontal: gridRowPadding },
              effectiveDisplayMode === 'grid' &&
                !rest.horizontal &&
                maxGridWidth !== undefined && { width: '100%', maxWidth: maxGridWidth, alignSelf: 'center' },
              rest.columnWrapperStyle,
            ],
          }}
        />
      )}
    </View>
  );
}

function getOfferCardComponent(isSmallUI: boolean, useHorizontalCard: boolean) {
  return isSmallUI ? ListingOfferCard : useHorizontalCard ? ListingOfferCardHorizontal : ListingOfferCardLarge;
}

function getListingResultsTranslationKey(
  i18nKey: string | null | undefined,
  filters: (FindAllListingsDTO & { isLoggedIn?: boolean }) | undefined,
) {
  const resultsKeySuffix = i18nKey ?? filters?.type;
  const resultsTranslationKey = resultsKeySuffix ? `numResults.${resultsKeySuffix}` : 'numResults.default';
  return resultsTranslationKey;
}

function getListingRendererProps(
  { horizontal, initialNumToRender, maxToRenderPerBatch }: ListingListProps,
  benchmarkFlashList: boolean | undefined,
  numColumns: number,
) {
  const isNativeVerticalList = isMobileNative && !horizontal;
  return {
    // PARTAGE-CLUB-WEB-TC / TF: web and native vertical feeds can crash in FlashList's
    // synchronous layout commit loop. Use the supported FlatList renderer
    // until the underlying layout trigger is isolated; keep benchmarks explicit.
    listType: !horizontal && !benchmarkFlashList ? PageListComponentType.FLAT : PageListComponentType.FLASH,
    // FlatList batches count rows, so a grid otherwise mounts 20 cards at once.
    initialNumToRender: initialNumToRender ?? (isNativeVerticalList ? Math.ceil(6 / numColumns) : undefined),
    maxToRenderPerBatch: maxToRenderPerBatch ?? (isNativeVerticalList ? 2 : undefined),
  };
}

function ListingPageResults({
  rest,
  benchmarkFlashList,
  numColumns,
  withPadding,
  separator,
  listingListState,
  isSearchActive,
  items,
  t,
  ListEmptyComponent,
  alwaysShowFooter,
  ListFooterComponent,
  updatedListHeaderComponent,
  renderedItems,
  effectiveInnerRef,
  providedKeyExtractor,
  isUsingBenchmarkData,
  shouldHandleScroll,
  handleScroll,
  handleScrollBeginDrag,
  renderListingItem,
  showVerticalScrollIndicator,
}: {
  rest: ListingListProps;
  benchmarkFlashList: boolean | undefined;
  numColumns: number;
  withPadding: boolean | undefined;
  separator: () => JSX.Element;
  listingListState: ListingListState;
  isSearchActive: boolean;
  items: Listing[];
  t: ReturnType<typeof useTranslation<'all-listings'>>['t'];
  ListEmptyComponent: ListingListProps['ListEmptyComponent'];
  alwaysShowFooter: boolean;
  ListFooterComponent: ListingListProps['ListFooterComponent'];
  updatedListHeaderComponent: JSX.Element;
  renderedItems: Listing[];
  effectiveInnerRef: RefObject<FlashOrFlatList<Listing, null>>;
  providedKeyExtractor:
    (((item: Listing, index: number) => string) & ((item: Listing, index: number) => string)) | undefined;
  isUsingBenchmarkData: boolean;
  shouldHandleScroll: boolean;
  handleScroll: (event: NativeSyntheticEvent<NativeScrollEvent>) => void;
  handleScrollBeginDrag: (event: NativeSyntheticEvent<NativeScrollEvent>) => void;
  renderListingItem: ({ item, index }: { item: Listing; index: number }) => JSX.Element;
  showVerticalScrollIndicator: boolean;
}) {
  return (
    <ListPage
      {...rest}
      {...getListingRendererProps(rest, benchmarkFlashList, numColumns)}
      numColumns={numColumns}
      columnWrapperStyle={[{ justifyContent: 'flex-start' }, rest.columnWrapperStyle]}
      withPadding={withPadding}
      decelerationRate={0.5}
      ItemSeparatorComponent={separator}
      ListEmptyComponent={
        listingListState.isLoading ? (
          <ListingSkeletons horizontal={rest.horizontal ?? false} count={5} />
        ) : isSearchActive && items.length === 0 ? (
          <Text color="text.tertiary" marginTop="spacing.general.x4" textAlign="center" variant="bodyBase">
            {t('partialSearchBar.empty')}
          </Text>
        ) : (
          ListEmptyComponent
        )
      }
      ListFooterComponent={
        listingListState.isFetchingNextPage ? (
          <Column marginTop="spacing.general.x8">
            <ActivityIndicator />
          </Column>
        ) : items.length > 0 || alwaysShowFooter ? (
          ListFooterComponent
        ) : null
      }
      ListHeaderComponent={updatedListHeaderComponent}
      data={renderedItems}
      innerRef={effectiveInnerRef}
      keyExtractor={(item, index) => {
        const baseKey = providedKeyExtractor?.(item, index) ?? item.id;
        return isUsingBenchmarkData ? `${baseKey}:${index.toString()}` : baseKey;
      }}
      onEndReached={listingListState.onEndReached}
      onEndReachedThreshold={0.8}
      onRefresh={listingListState.onRefresh}
      onScroll={shouldHandleScroll ? handleScroll : undefined}
      onScrollBeginDrag={handleScrollBeginDrag}
      refreshing={listingListState.refreshing}
      renderItem={renderListingItem}
      scrollEventThrottle={16}
      showsVerticalScrollIndicator={showVerticalScrollIndicator}
    />
  );
}

type ListingResultsHeaderProps = {
  ListHeaderComponent: ListingListProps['ListHeaderComponent'];
  maxGridWidth: ListingListProps['maxGridWidth'];
  showButtonsNavigation: boolean;
  effectiveDisplayMode: string;
  filterCategory: string | undefined;
  onCategoryPress: ((categoryId: string) => void) | undefined;
  categoryName: string | undefined;
  onNextButtonPressed: (type: 'next' | 'previous') => void;
  showSearchBar: boolean;
  onSearchQueryChange: Dispatch<SetStateAction<string>> | undefined;
  searchQuery: string;
  searchAsYouType: boolean;
  numListingsTotal: number;
  isSmallUI: boolean;
  showResultsCount: boolean;
  allowSort: boolean | undefined;
  resultsHeaderActions: React.ReactNode;
  t: ReturnType<typeof useTranslation<'all-listings'>>['t'];
  resultsTranslationKey: string;
  setListFormat: Dispatch<SetStateAction<'list' | 'grid'>> | undefined;
  filters: (FindAllListingsDTO & { isLoggedIn?: boolean }) | undefined;
  isDisplayListForced: boolean;
  filterSortBy: ListingsSortBy | undefined;
  doShowSortOptions: () => void;
  canPublish: boolean;
  showOffersBanner: boolean;
  showRequestsBanner: boolean;
  benchmarkFlashList: boolean | undefined;
  isBenchmarkReady: boolean;
  isBenchmarkRunning: boolean;
  onStartBenchmark: () => void;
};

function ListingResultsHeader(props: ListingResultsHeaderProps) {
  const {
    ListHeaderComponent,
    maxGridWidth,
    showButtonsNavigation,
    effectiveDisplayMode,
    filterCategory,
    onCategoryPress,
    categoryName,
    onNextButtonPressed,
    showSearchBar,
    onSearchQueryChange,
    searchQuery,
    searchAsYouType,
    numListingsTotal,
    isSmallUI,
    showResultsCount,
    allowSort,
    resultsHeaderActions,
    t,
    resultsTranslationKey,
    setListFormat,
    filters,
    isDisplayListForced,
    filterSortBy,
    doShowSortOptions,
    canPublish,
    showOffersBanner,
    showRequestsBanner,
    benchmarkFlashList,
    isBenchmarkReady,
    isBenchmarkRunning,
    onStartBenchmark,
  } = props;
  if (!hasListingResultsHeaderContent(props)) return null;

  return (
    // Use layout gaps here: optional header components can render null, and
    // Column's gap prop would still insert spacer views around those components.
    <Column
      style={[
        { rowGap: theme.spacing['spacing.general.x2'] },
        maxGridWidth !== undefined && { width: '100%', maxWidth: maxGridWidth, alignSelf: 'center' },
      ]}
      marginBottom="spacing.general.x4"
    >
      {React.isValidElement(ListHeaderComponent)
        ? ListHeaderComponent
        : typeof ListHeaderComponent === 'function'
          ? React.createElement(ListHeaderComponent)
          : null}

      <ListingCarouselNavigation
        showButtonsNavigation={showButtonsNavigation}
        effectiveDisplayMode={effectiveDisplayMode}
        filterCategory={filterCategory}
        onCategoryPress={onCategoryPress}
        categoryName={categoryName}
        onNextButtonPressed={onNextButtonPressed}
      />
      {showSearchBar && onSearchQueryChange ? (
        <PartialSearchBar value={searchQuery} onSearch={onSearchQueryChange} searchAsYouType={searchAsYouType} />
      ) : null}

      <ListingResultsCount
        numListingsTotal={numListingsTotal}
        isSmallUI={isSmallUI}
        showResultsCount={showResultsCount}
        allowSort={allowSort}
        resultsHeaderActions={resultsHeaderActions}
        t={t}
        resultsTranslationKey={resultsTranslationKey}
      />
      <ListingViewControls
        setListFormat={setListFormat}
        allowSort={allowSort}
        numListingsTotal={numListingsTotal}
        filters={filters}
        isDisplayListForced={isDisplayListForced}
        t={t}
        effectiveDisplayMode={effectiveDisplayMode}
        filterSortBy={filterSortBy}
        doShowSortOptions={doShowSortOptions}
      />

      <ListingPublicationBanner
        canPublish={canPublish}
        filters={filters}
        showOffersBanner={showOffersBanner}
        allowSort={allowSort}
        numListingsTotal={numListingsTotal}
      />

      {canPublish && filters?.type === ListingType.request && showRequestsBanner ? (
        <Column marginTop={allowSort && numListingsTotal > 0 ? 'spacing.general.x2' : 'spacing.general.0'}>
          <CreateRequestBanner />
        </Column>
      ) : null}

      <ListingBenchmarkControl
        benchmarkFlashList={benchmarkFlashList}
        isBenchmarkReady={isBenchmarkReady}
        isBenchmarkRunning={isBenchmarkRunning}
        onStartBenchmark={onStartBenchmark}
      />
    </Column>
  );
}

function hasListingResultsHeaderContent({
  ListHeaderComponent,
  showButtonsNavigation,
  effectiveDisplayMode,
  filterCategory,
  showSearchBar,
  onSearchQueryChange,
  numListingsTotal,
  isSmallUI,
  showResultsCount,
  allowSort,
  resultsHeaderActions,
  setListFormat,
  canPublish,
  filters,
  showOffersBanner,
  showRequestsBanner,
  benchmarkFlashList,
}: ListingResultsHeaderProps): boolean {
  const hasCustomHeader = React.isValidElement(ListHeaderComponent) || typeof ListHeaderComponent === 'function';
  const hasCarouselNavigation =
    showButtonsNavigation && effectiveDisplayMode === 'grid' && filterCategory !== undefined;
  const hasSearchBar = showSearchBar && onSearchQueryChange;
  const hasResultsCount = numListingsTotal > 0 && (isSmallUI || showResultsCount || allowSort || resultsHeaderActions);
  const hasViewControls = setListFormat || (allowSort && numListingsTotal > 0);
  const hasPublicationBanner =
    canPublish &&
    ((filters?.type === ListingType.offer && showOffersBanner) ||
      (filters?.type === ListingType.request && showRequestsBanner));

  return [
    hasCustomHeader,
    hasCarouselNavigation,
    hasSearchBar,
    hasResultsCount,
    hasViewControls,
    hasPublicationBanner,
    benchmarkFlashList,
  ].some(Boolean);
}

function ListingBenchmarkControl({
  benchmarkFlashList,
  isBenchmarkReady,
  isBenchmarkRunning,
  onStartBenchmark,
}: Pick<
  ListingResultsHeaderProps,
  'benchmarkFlashList' | 'isBenchmarkReady' | 'isBenchmarkRunning' | 'onStartBenchmark'
>) {
  return benchmarkFlashList ? (
    <Row marginTop="spacing.general.x2">
      <Button disabled={!isBenchmarkReady || isBenchmarkRunning} onPress={onStartBenchmark} variant="secondary">
        <Text>{isBenchmarkRunning ? 'FlashList benchmark running…' : 'Run FlashList benchmark'}</Text>
      </Button>
    </Row>
  ) : null;
}

function ListingPublicationBanner({
  canPublish,
  filters,
  showOffersBanner,
  allowSort,
  numListingsTotal,
}: {
  canPublish: boolean;
  filters: (FindAllListingsDTO & { isLoggedIn?: boolean }) | undefined;
  showOffersBanner: boolean;
  allowSort: boolean | undefined;
  numListingsTotal: number;
}): React.ReactNode {
  return canPublish && filters?.type === ListingType.offer && showOffersBanner ? (
    <Column marginTop={allowSort && numListingsTotal > 0 ? 'spacing.general.x2' : 'spacing.general.0'}>
      <CreateOfferBanner />
    </Column>
  ) : null;
}

function ListingCarouselNavigation({
  showButtonsNavigation,
  effectiveDisplayMode,
  filterCategory,
  onCategoryPress,
  categoryName,
  onNextButtonPressed,
}: {
  showButtonsNavigation: boolean;
  effectiveDisplayMode: string;
  filterCategory: string | undefined;
  onCategoryPress: ((categoryId: string) => void) | undefined;
  categoryName: string | undefined;
  onNextButtonPressed: (type: 'next' | 'previous') => void;
}): React.ReactNode {
  if (!showButtonsNavigation || effectiveDisplayMode !== 'grid' || filterCategory === undefined) return null;

  return (
    <ListingCategoryNavigation
      filterCategory={filterCategory}
      onCategoryPress={onCategoryPress}
      categoryName={categoryName}
      onNextButtonPressed={onNextButtonPressed}
    />
  );
}

function ListingResultsCount({
  numListingsTotal,
  isSmallUI,
  showResultsCount,
  allowSort,
  resultsHeaderActions,
  t,
  resultsTranslationKey,
}: {
  numListingsTotal: number;
  isSmallUI: boolean;
  showResultsCount: boolean;
  allowSort: boolean | undefined;
  resultsHeaderActions: React.ReactNode;
  t: ReturnType<typeof useTranslation<'all-listings'>>['t'];
  resultsTranslationKey: string;
}): React.ReactNode {
  return numListingsTotal > 0 && (isSmallUI || showResultsCount || allowSort || resultsHeaderActions) ? (
    <Row
      alignItems="center"
      flexWrap="wrap"
      justifyContent="space-between"
      paddingVertical={showResultsCount ? 'spacing.general.x2' : undefined}
      style={{ rowGap: theme.spacing['spacing.general.x2'] }}
    >
      {isSmallUI || showResultsCount ? (
        <Text fontSize={20} fontWeight="600" lineHeight={24}>
          {t(resultsTranslationKey, {
            count: numListingsTotal,
          })}
        </Text>
      ) : null}
      {resultsHeaderActions}
    </Row>
  ) : null;
}

function ListingViewControls({
  setListFormat,
  allowSort,
  numListingsTotal,
  filters,
  isDisplayListForced,
  t,
  effectiveDisplayMode,
  filterSortBy,
  doShowSortOptions,
}: {
  setListFormat: Dispatch<SetStateAction<'list' | 'grid'>> | undefined;
  allowSort: boolean | undefined;
  numListingsTotal: number;
  filters: (FindAllListingsDTO & { isLoggedIn?: boolean }) | undefined;
  isDisplayListForced: boolean;
  t: ReturnType<typeof useTranslation<'all-listings'>>['t'];
  effectiveDisplayMode: string;
  filterSortBy: ListingsSortBy | undefined;
  doShowSortOptions: () => void;
}): React.ReactNode {
  return setListFormat || (allowSort && numListingsTotal > 0) ? (
    <Row justifyContent="flex-end" alignItems="center" gap="spacing.general.x1">
      {setListFormat && filters?.type === ListingType.offer && !isDisplayListForced ? (
        <Row gap="spacing.general.x4" alignItems="center" style={{ marginRight: 'auto' }}>
          <Pressable
            accessibilityLabel={t('list.toggleViewMode', { ns: 'accessibility' })}
            onPress={() => setListFormat(effectiveDisplayMode === 'list' ? 'grid' : 'list')}
          >
            {effectiveDisplayMode === 'list' ? (
              <IconLayoutColumns
                strokeWidth={ICON_STROKE_WIDTH}
                color={theme.colors['text.primary'] || theme.colors['text.tertiary']}
              />
            ) : (
              <IconList
                strokeWidth={ICON_STROKE_WIDTH}
                color={theme.colors['text.primary'] || theme.colors['text.tertiary']}
              />
            )}
          </Pressable>
        </Row>
      ) : null}
      {allowSort && numListingsTotal > 0 && filterSortBy ? (
        <Pressable
          onPress={doShowSortOptions}
          style={{
            borderColor: theme.colors['surface.inverse.tertiary-neutral'],
            borderWidth: 2,
            borderRadius: 20,
            paddingHorizontal: 16,
            paddingVertical: 4,
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Row alignItems="center" gap="spacing.general.x1">
            <IconArrowsSort strokeWidth={ICON_STROKE_WIDTH} />
            <Text>{t(`sortBy.${filterSortBy}`)}</Text>
          </Row>
        </Pressable>
      ) : null}
    </Row>
  ) : null;
}

type ListingListItemProps = Pick<
  ListingListProps,
  | 'hideListingsSubtype'
  | 'isSelecting'
  | 'selectedRowsState'
  | 'showUser'
  | 'onBlockDates'
  | 'onChangeLoanFee'
  | 'onHide'
  | 'onUnhide'
  | 'onDelete'
  | 'setListingForMenuAction'
  | 'showHighestCard'
  | 'onListingHoverChange'
  | 'highlightedListingId'
  | 'horizontalGap'
> & {
  item: Listing;
  index: number;
  inlinePromptByPosition: Map<number, JSX.Element> | undefined;
  effectiveDisplayMode: 'list' | 'grid';
  gridColumnWidth: number | undefined;
  isMenuItemAvailable: (
    listing: Listing,
    what: 'blockDates' | 'changeLoanFee' | 'hide' | 'unhide' | 'delete',
  ) => boolean;
  CardOverride: ListingListProps['card'];
  gridItemAlignSelf: 'center' | 'flex-start';
  shouldMeasureGridCard: boolean;
  onGridCardLayout: (event: LayoutChangeEvent) => void;
  skipHeightUniformity: boolean;
  isLargeUI: boolean;
  OfferCardComponent: ComponentType<ListingOfferCardProps>;
  highlightedListingCardStyle: ViewStyle;
};

function ListingListItem({
  item,
  index,
  inlinePromptByPosition,
  effectiveDisplayMode,
  gridColumnWidth,
  hideListingsSubtype,
  isSelecting,
  selectedRowsState,
  showUser,
  isMenuItemAvailable,
  onBlockDates,
  onChangeLoanFee,
  onHide,
  onUnhide,
  onDelete,
  setListingForMenuAction,
  CardOverride,
  showHighestCard,
  gridItemAlignSelf,
  shouldMeasureGridCard,
  onGridCardLayout,
  skipHeightUniformity,
  isLargeUI,
  OfferCardComponent,
  onListingHoverChange,
  highlightedListingId,
  highlightedListingCardStyle,
  horizontalGap,
}: ListingListItemProps) {
  const inlinePrompt = isMobileNative ? inlinePromptByPosition?.get(index + 1) : undefined;
  const offerCardProps: ListingOfferCardProps = {
    listing: item,
    columnWidth: effectiveDisplayMode === 'grid' ? gridColumnWidth : undefined,
    hideListingsSubtype,
    isSelecting,
    selectedRowsState,
    showUser,
    onBlockDates: isMenuItemAvailable(item, 'blockDates') ? onBlockDates : undefined,
    onChangeLoanFee: isMenuItemAvailable(item, 'changeLoanFee') ? onChangeLoanFee : undefined,
    onHide: isMenuItemAvailable(item, 'hide') ? onHide : undefined,
    onUnhide: isMenuItemAvailable(item, 'unhide') ? onUnhide : undefined,
    onDelete: isMenuItemAvailable(item, 'delete') ? onDelete : undefined,
    setListingForMenuAction,
  };
  const hoverProps = onListingHoverChange
    ? {
        onPointerEnter: () => onListingHoverChange(item),
        onPointerLeave: () => onListingHoverChange(undefined),
      }
    : undefined;

  return (
    <HighlightableListingCard
      {...hoverProps}
      highlightedStyle={highlightedListingCardStyle}
      isDimmed={Boolean(highlightedListingId && highlightedListingId !== item.id)}
      isHighlighted={highlightedListingId === item.id}
    >
      {inlinePrompt ? <View style={{ marginBottom: 15 }}>{inlinePrompt}</View> : null}
      <ListingListItemCard
        CardOverride={CardOverride}
        OfferCardComponent={OfferCardComponent}
        effectiveDisplayMode={effectiveDisplayMode}
        gridColumnWidth={gridColumnWidth}
        gridItemAlignSelf={gridItemAlignSelf}
        horizontalGap={horizontalGap}
        index={index}
        isLargeUI={isLargeUI}
        item={item}
        offerCardProps={offerCardProps}
        onGridCardLayout={onGridCardLayout}
        shouldMeasureGridCard={shouldMeasureGridCard}
        showHighestCard={showHighestCard}
        skipHeightUniformity={skipHeightUniformity}
      />
    </HighlightableListingCard>
  );
}

function ListingListItemCard({
  CardOverride,
  OfferCardComponent,
  effectiveDisplayMode,
  gridColumnWidth,
  gridItemAlignSelf,
  horizontalGap,
  index,
  isLargeUI,
  item,
  offerCardProps,
  onGridCardLayout,
  shouldMeasureGridCard,
  showHighestCard,
  skipHeightUniformity,
}: Pick<
  ListingListItemProps,
  | 'CardOverride'
  | 'OfferCardComponent'
  | 'effectiveDisplayMode'
  | 'gridColumnWidth'
  | 'gridItemAlignSelf'
  | 'horizontalGap'
  | 'index'
  | 'isLargeUI'
  | 'item'
  | 'onGridCardLayout'
  | 'shouldMeasureGridCard'
  | 'showHighestCard'
  | 'skipHeightUniformity'
> & { offerCardProps: ListingOfferCardProps }) {
  if (CardOverride && item.type === ListingType.offer) {
    const card = <CardOverride {...offerCardProps} />;
    return effectiveDisplayMode === 'grid' ? (
      <Box
        alignSelf={gridItemAlignSelf}
        onLayout={shouldMeasureGridCard ? onGridCardLayout : undefined}
        width={gridColumnWidth}
        style={horizontalGap ? { paddingHorizontal: theme.spacing[horizontalGap] / 2 } : undefined}
      >
        {card}
      </Box>
    ) : (
      card
    );
  }

  if (item.type === ListingType.request) {
    return (
      <ListingRequestListItemCard
        effectiveDisplayMode={effectiveDisplayMode}
        gridColumnWidth={gridColumnWidth}
        gridItemAlignSelf={gridItemAlignSelf}
        hideListingsSubtype={offerCardProps.hideListingsSubtype}
        index={index}
        item={item}
        onGridCardLayout={onGridCardLayout}
        shouldMeasureGridCard={shouldMeasureGridCard}
        showHighestCard={showHighestCard}
        showUser={offerCardProps.showUser}
        skipHeightUniformity={skipHeightUniformity}
      />
    );
  }

  return (
    <ListingOfferListItemCard
      OfferCardComponent={OfferCardComponent}
      effectiveDisplayMode={effectiveDisplayMode}
      gridColumnWidth={gridColumnWidth}
      gridItemAlignSelf={gridItemAlignSelf}
      index={index}
      isLargeUI={isLargeUI}
      offerCardProps={offerCardProps}
      onGridCardLayout={onGridCardLayout}
      shouldMeasureGridCard={shouldMeasureGridCard}
    />
  );
}

function ListingRequestListItemCard({
  effectiveDisplayMode,
  gridColumnWidth,
  gridItemAlignSelf,
  hideListingsSubtype,
  index,
  item,
  onGridCardLayout,
  shouldMeasureGridCard,
  showHighestCard,
  showUser,
  skipHeightUniformity,
}: Pick<
  ListingListItemProps,
  | 'effectiveDisplayMode'
  | 'gridColumnWidth'
  | 'gridItemAlignSelf'
  | 'hideListingsSubtype'
  | 'index'
  | 'item'
  | 'onGridCardLayout'
  | 'shouldMeasureGridCard'
  | 'showHighestCard'
  | 'showUser'
  | 'skipHeightUniformity'
>) {
  if (!showHighestCard) {
    return (
      <ListingRequestCard
        listing={item}
        isFirst={index === 0}
        showUser={showUser}
        hideListingsSubtype={hideListingsSubtype}
        skipHeightUniformity={skipHeightUniformity}
      />
    );
  }

  const card = (
    <ListingRequestCardHighest
      listing={item}
      columnWidth={gridColumnWidth}
      isFirst={index === 0}
      showUser={showUser}
      hideListingsSubtype={hideListingsSubtype}
    />
  );
  return effectiveDisplayMode === 'grid' ? (
    <Box
      alignSelf={gridItemAlignSelf}
      onLayout={shouldMeasureGridCard ? onGridCardLayout : undefined}
      width={gridColumnWidth}
    >
      {card}
    </Box>
  ) : (
    card
  );
}

function ListingOfferListItemCard({
  OfferCardComponent,
  effectiveDisplayMode,
  gridColumnWidth,
  gridItemAlignSelf,
  index,
  isLargeUI,
  offerCardProps,
  onGridCardLayout,
  shouldMeasureGridCard,
}: Pick<
  ListingListItemProps,
  | 'OfferCardComponent'
  | 'effectiveDisplayMode'
  | 'gridColumnWidth'
  | 'gridItemAlignSelf'
  | 'index'
  | 'isLargeUI'
  | 'onGridCardLayout'
  | 'shouldMeasureGridCard'
> & { offerCardProps: ListingOfferCardProps }) {
  if (isLargeUI) {
    const card = (
      <OfferCardWithTutorial CardComponent={ListingOfferCardLarge} index={index} offerCardProps={offerCardProps} />
    );
    return effectiveDisplayMode === 'grid' ? (
      <Box
        alignSelf={gridItemAlignSelf}
        onLayout={shouldMeasureGridCard ? onGridCardLayout : undefined}
        width={gridColumnWidth}
      >
        {card}
      </Box>
    ) : (
      card
    );
  }

  if (!isMobileNative && effectiveDisplayMode === 'grid') {
    return (
      <Box
        alignSelf={gridItemAlignSelf}
        onLayout={shouldMeasureGridCard ? onGridCardLayout : undefined}
        width={gridColumnWidth}
      >
        <OfferCardWithTutorial CardComponent={OfferCardComponent} index={index} offerCardProps={offerCardProps} />
      </Box>
    );
  }

  if (effectiveDisplayMode === 'list') {
    return <ListingOfferCardSmallOld {...offerCardProps} isFirst={index === 0} />;
  }

  return (
    <Box alignSelf={gridItemAlignSelf} onLayout={onGridCardLayout} width={gridColumnWidth}>
      <ListingOfferCardSmall {...offerCardProps} isFirst={index === 0} />
    </Box>
  );
}

function OfferCardWithTutorial({
  CardComponent,
  index,
  offerCardProps,
}: {
  CardComponent: ComponentType<ListingOfferCardProps>;
  index: number;
  offerCardProps: ListingOfferCardProps;
}) {
  return index === 0 ? (
    <FirstOfferTutorialCard CardComponent={CardComponent} {...offerCardProps} />
  ) : (
    <CardComponent {...offerCardProps} />
  );
}

function ListingCategoryNavigation({
  filterCategory,
  onCategoryPress,
  categoryName,
  onNextButtonPressed,
}: Pick<ListingListProps, 'onCategoryPress' | 'categoryName'> & {
  filterCategory: NonNullable<FindAllListingsDTO['category']>;
  onNextButtonPressed: (type: 'next' | 'previous') => void;
}) {
  return (
    <Pressable
      onPress={() => {
        onCategoryPress?.(filterCategory);
      }}
    >
      <Row justifyContent="space-between" alignItems="center" marginBottom="spacing.general.x2">
        <Text variant="bodyLarge" fontWeight="700">
          {categoryName ? categoryName : null}
        </Text>

        <Row gap="spacing.general.x3">
          <Button
            width={40}
            height={40}
            variant="primary"
            justifyContent="center"
            alignItems="center"
            style={{
              zIndex: 1,
              backgroundColor: theme.colors['text.primary'],
              borderRadius: 20,
              shadowColor: '#000',
              shadowOffset: { width: 0, height: 2 },
              shadowOpacity: 0.1,
              shadowRadius: 4,
              elevation: 3,
            }}
            onPress={() => {
              onNextButtonPressed('previous');
            }}
          >
            <IconChevronLeft strokeWidth={ICON_STROKE_WIDTH} size={24} color="white" />
          </Button>

          <Button
            width={40}
            height={40}
            variant="primary"
            justifyContent="center"
            alignItems="center"
            style={{
              zIndex: 1,
              backgroundColor: theme.colors['text.primary'],
              borderRadius: 20,
              shadowColor: '#000',
              shadowOffset: { width: 0, height: 2 },
              shadowOpacity: 0.1,
              shadowRadius: 4,
              elevation: 3,
            }}
            onPress={() => {
              onNextButtonPressed('next');
            }}
          >
            <IconChevronRight strokeWidth={ICON_STROKE_WIDTH} size={24} color="white" />
          </Button>
        </Row>
      </Row>
    </Pressable>
  );
}

function useListingGridLayout({
  effectiveDisplayMode,
  filters,
  useHorizontalCard,
  showHighestCard,
  isSmallUI,
  isLargeUI,
  withPadding,
  maxGridWidth,
  ...rest
}: Pick<
  ListingListProps,
  | 'filters'
  | 'useHorizontalCard'
  | 'showHighestCard'
  | 'withPadding'
  | 'maxGridWidth'
  | 'horizontal'
  | 'numColumns'
  | 'itemHorizontalSpacing'
  | 'columnWrapperStyle'
> & { effectiveDisplayMode: 'list' | 'grid'; isSmallUI: boolean; isLargeUI: boolean }) {
  const { left: insetLeft, right: insetRight } = useSafeAreaInsets();
  const listHorizontalPaddingFallback = theme.spacing['spacing.general.x4'];
  const [listWidth, setListWidth] = useState(0);
  const gridMeasurementKey = `${effectiveDisplayMode}:${filters?.type ?? ''}:${String(useHorizontalCard)}:${String(showHighestCard)}`;
  const [gridMeasurement, setGridMeasurement] = useState<{ key: string; width: number }>();
  const measuredGridCardWidth = gridMeasurement?.key === gridMeasurementKey ? gridMeasurement.width : undefined;
  const onGridCardLayout = useCallback(
    (event: LayoutChangeEvent) => {
      if (measuredGridCardWidth !== undefined) return;
      const nextWidth = Math.round(event.nativeEvent.layout.width);
      if (!nextWidth) return;
      setGridMeasurement({ key: gridMeasurementKey, width: nextWidth });
    },
    [gridMeasurementKey, measuredGridCardWidth],
  );

  const gridCardWidth = useMemo<number | undefined>(() => {
    if (effectiveDisplayMode === 'list') return undefined;
    if (useHorizontalCard) return undefined;

    if (isMobileNative || isSmallUI) return SMALL_OFFER_CARD_GRID_WIDTH;

    return LARGE_OFFER_CARD_GRID_WIDTH;
  }, [effectiveDisplayMode, isSmallUI, useHorizontalCard]);

  const spacing =
    rest.itemHorizontalSpacing ??
    (isLargeUI ? theme.spacing['spacing.general.x8'] : theme.spacing['spacing.general.x10']);
  const effectiveAvailableWidth = useMemo(() => {
    const horizontalPadding =
      withPadding && !rest.horizontal
        ? (insetLeft || listHorizontalPaddingFallback) + (insetRight || listHorizontalPaddingFallback)
        : 0;
    const availableWidth = Math.max(0, listWidth - horizontalPadding);
    return effectiveDisplayMode === 'grid' && !rest.horizontal
      ? Math.min(availableWidth, maxGridWidth ?? availableWidth)
      : availableWidth;
  }, [
    effectiveDisplayMode,
    insetLeft,
    insetRight,
    listHorizontalPaddingFallback,
    listWidth,
    maxGridWidth,
    rest.horizontal,
    withPadding,
  ]);

  const numColumns = useMemo(() => {
    if (rest.numColumns) return rest.numColumns;
    if (isMobileNative && effectiveDisplayMode === 'grid' && filters?.type === ListingType.offer)
      return useHorizontalCard ? 1 : 2;
    if (!gridCardWidth) return 1;

    const availableWidth = effectiveAvailableWidth;
    const columnGap: number = spacing;
    const cardFootprint: number = gridCardWidth + columnGap;
    const maxResponsiveColumnWidth: number = gridCardWidth + columnGap;
    const minColumnsForMaxWidth: number = Math.ceil(
      (availableWidth + columnGap) / (maxResponsiveColumnWidth + columnGap),
    );

    return Math.max(1, Math.max(Math.floor((availableWidth + columnGap) / cardFootprint), minColumnsForMaxWidth));
  }, [
    effectiveAvailableWidth,
    effectiveDisplayMode,
    filters?.type,
    gridCardWidth,
    rest.numColumns,
    spacing,
    useHorizontalCard,
  ]);

  const maxGridColumnWidth = useMemo<number | undefined>(() => {
    if (!gridCardWidth) return undefined;

    return Math.round(gridCardWidth * MAX_GRID_CARD_WIDTH_MULTIPLIER);
  }, [gridCardWidth]);

  const gridColumnWidth = useMemo<number | undefined>(() => {
    if (!gridCardWidth) return undefined;

    const totalGapWidth = spacing * (numColumns - 1);
    const responsiveColumnWidth = Math.max(0, (effectiveAvailableWidth - totalGapWidth) / numColumns);

    // A bounded grid divides its row equally, including partially filled rows.
    if (maxGridWidth !== undefined && rest.numColumns && !rest.horizontal) return responsiveColumnWidth;

    return Math.min(responsiveColumnWidth, maxGridColumnWidth ?? responsiveColumnWidth);
  }, [
    effectiveAvailableWidth,
    gridCardWidth,
    maxGridColumnWidth,
    maxGridWidth,
    numColumns,
    rest.horizontal,
    rest.numColumns,
    spacing,
  ]);

  const columnStyle = StyleSheet.flatten(rest.columnWrapperStyle);
  const renderedGap = columnStyle?.columnGap ?? theme.spacing['spacing.general.x4'];
  // Center the full column footprint, including empty slots in the last row.
  // The sizing allowance can exceed the rendered gap or hit the card-width cap.
  const gridRowPadding =
    !rest.horizontal && gridColumnWidth !== undefined && numColumns > 1 && typeof renderedGap === 'number'
      ? Math.max(0, (effectiveAvailableWidth - gridColumnWidth * numColumns - renderedGap * (numColumns - 1)) / 2)
      : 0;

  const gridItemAlignSelf = gridColumnWidth !== undefined ? ('center' as const) : ('flex-start' as const);

  const shouldMeasureGridCard = effectiveDisplayMode === 'grid';

  return {
    listWidth,
    setListWidth,
    measuredGridCardWidth,
    onGridCardLayout,
    numColumns,
    gridColumnWidth,
    gridRowPadding,
    gridItemAlignSelf,
    shouldMeasureGridCard,
  };
}

function useListingSort({
  filters,
  sortState,
  sortByStartDate,
}: Pick<ListingListProps, 'filters' | 'sortState' | 'sortByStartDate'>) {
  const [showSortOptions, setShowSortOptions] = useState(false);

  const [sortBy, setSortBy] = sortState ?? [];

  const sortOptions = useMemo(
    () => [
      ...((filters?.search?.trim().length ?? 0) > 0 ? [ListingsSortBy.Relevance] : []),
      ...(sortByStartDate
        ? [ListingsSortBy.StartDate, ListingsSortBy.Distance, ListingsSortBy.Date]
        : filters?.type === ListingType.offer
          ? [ListingsSortBy.Distance, ListingsSortBy.Date, ListingsSortBy.Popularity, ListingsSortBy.Random]
          : [ListingsSortBy.Distance, ListingsSortBy.Date, ListingsSortBy.Urgency]),
    ],
    [filters?.search, filters?.type, sortByStartDate],
  );

  const doShowSortOptions = () => {
    setShowSortOptions(true);
    if (filters?.type) {
      track(Event.ClickedSortBabillard, { listingType: filters.type });
    }
  };

  const doSort = (by: ListingsSortBy) => {
    setSortBy?.(by);
    setShowSortOptions(false);
    if (filters?.type) {
      track(Event.DidSortBabillard, { listingType: filters.type, sortBy: by });
    }
  };

  return { showSortOptions, setShowSortOptions, sortBy, sortOptions, doShowSortOptions, doSort };
}

function ListingSortSheet({ sorting }: { sorting: ReturnType<typeof useListingSort> }) {
  const { t } = useTranslation('all-listings');
  const { showSortOptions, setShowSortOptions, sortBy, sortOptions, doSort } = sorting;
  return (
    <BottomSheet onClose={() => setShowSortOptions(false)} open={showSortOptions}>
      <Column gap="spacing.general.x8" paddingHorizontal="spacing.general.x4" paddingTop="spacing.general.x4">
        <Text fontWeight="600" textAlign="center" variant="headerXSmall">
          {t('sortTitle')}
        </Text>

        <Column gap="spacing.general.x4" marginHorizontal="spacing.general.x4">
          {sortOptions.map((by) => (
            <Button
              key={by}
              onPress={() => doSort(by)}
              pressableType={PressableType.GestureHandler}
              style={{
                height: 48,
                borderRadius: 24,
                padding: 13,
                borderWidth: sortBy === by ? 0 : 1,
                borderColor: theme.colors['surface.inverse.tertiary-neutral'],
                backgroundColor: sortBy === by ? theme.colors['surface.tertiary'] : undefined,
              }}
              variant="plain"
            >
              <Text
                color="text.secondary"
                flex={1}
                fontWeight={sortBy === by ? '700' : '400'}
                textAlign="center"
                variant="bodyBase"
              >
                {t(`sortBy.${by}`)}
              </Text>
            </Button>
          ))}
        </Column>
      </Column>
    </BottomSheet>
  );
}

function useListingSelectionSync({
  items,
  selectAll,
  selectedRowsState,
  onSelectableListingIdsChange,
}: Pick<ListingListProps, 'selectAll' | 'selectedRowsState' | 'onSelectableListingIdsChange'> & { items: Listing[] }) {
  const itemIds = useMemo(() => items.map((item) => item.id), [items]);
  const previousItemIdsRef = useRef<ReadonlySet<Listing['id']>>(new Set());
  const wasSelectAllRef = useRef(false);
  const setSelectedRows = selectedRowsState?.[1];

  useEffect(() => {
    onSelectableListingIdsChange?.(itemIds);
  }, [itemIds, onSelectableListingIdsChange]);

  useEffect(() => {
    if (!setSelectedRows) return;

    setSelectedRows((selectedIds) =>
      mergeSelectedListingIds({
        currentItemIds: itemIds,
        previousItemIds: previousItemIdsRef.current,
        selectedIds,
        selectAll: Boolean(selectAll),
        wasSelectAll: wasSelectAllRef.current,
      }),
    );

    previousItemIdsRef.current = new Set(itemIds);
    wasSelectAllRef.current = Boolean(selectAll);
  }, [itemIds, selectAll, setSelectedRows]);
}

function useListingCardPresentation() {
  const { data: user } = useUser();

  const isMenuItemAvailable = useCallback(
    (listing: Listing, what: 'blockDates' | 'changeLoanFee' | 'hide' | 'unhide' | 'delete'): boolean => {
      if (what === 'blockDates') return isWhiteLabel();
      if (what === 'changeLoanFee') return isFeatureEnabled(FeatureFlag.loanFee, user?.featureFlags);
      if (what === 'hide') return Boolean(listing.addresses?.length || listing.groups?.length);
      if (what === 'unhide') {
        const canHide = Boolean(listing.addresses?.length || listing.groups?.length);
        return !canHide;
      }
      return true;
    },
    [user?.featureFlags],
  );

  const highlightedListingCardStyle = useMemo<ViewStyle>(
    () => ({
      zIndex: 2,
      borderRadius: theme.borderRadii['radius.lg'],
    }),
    [],
  );

  return { isMenuItemAvailable, highlightedListingCardStyle };
}

function useListingScroll({
  effectiveInnerRef,
  measuredGridCardWidth,
  onScroll,
  onScrollBeginDrag,
  itemCount,
}: Pick<ListingListProps, 'onScroll' | 'onScrollBeginDrag'> & {
  effectiveInnerRef: RefObject<FlashOrFlatList<Listing, null>>;
  measuredGridCardWidth: number | undefined;
  itemCount: number;
}) {
  const currentOffsetX = useRef(0);
  const onNextButtonPressed = (type: 'next' | 'previous') => {
    const step =
      measuredGridCardWidth !== undefined ? measuredGridCardWidth + theme.spacing['spacing.general.x4'] : 230;
    const remainder = currentOffsetX.current % step;
    if (remainder !== 0) {
      const baseOffset = currentOffsetX.current - remainder + (remainder > step / 2 ? step : 0);
      currentOffsetX.current = baseOffset;
    }

    effectiveInnerRef.current?.scrollToOffset({
      animated: true,
      offset: type === 'next' ? currentOffsetX.current + step : Math.max(0, currentOffsetX.current - step),
    });
  };

  const handleScrollBeginDrag = useCallback(
    (event: NativeSyntheticEvent<NativeScrollEvent>) => {
      Keyboard.dismiss();
      onScrollBeginDrag?.(event);
    },
    [onScrollBeginDrag],
  );

  const handleScroll = useCallback(
    (event: NativeSyntheticEvent<NativeScrollEvent>) => {
      currentOffsetX.current = event.nativeEvent.contentOffset.x;
      onScroll?.(event, itemCount);
    },
    [itemCount, onScroll],
  );
  return { onNextButtonPressed, handleScroll, handleScrollBeginDrag };
}
